import React from 'react';

import { faX, faY } from '@fortawesome/pro-regular-svg-icons';
import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { useListServiceProviders } from 'src/features/serviceProvider/api/useListServiceProviders';
import {
    BOOLEAN_MAP,
    BOOLEAN_OPTIONS,
    COUNTRY_TRANSLATION_MAP,
    SITE_TRANSLATION_MAP,
} from 'src/features/sites/utils/constants';
import { SiteFormSchemaValues } from 'src/features/sites/utils/formSchema';
import { siteMessages } from 'src/features/sites/utils/messages';
import { COUNTRY_MAP, SITE_TYPE } from 'src/features/sites/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import { FormFieldBlock, FormFieldBlockBody } from 'src/registry/components/blocks/FormFieldBlock';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { InputWithIcon } from 'src/registry/components/inputs/InputWithIcon';
import { InputWithInlineTitle } from 'src/registry/components/inputs/InputWithInlineTitle';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { generalMessages } from 'src/utils/messages';

type SiteFormProps = {
    form: UseFormReturn<SiteFormSchemaValues>;
    onSubmit: (values: SiteFormSchemaValues) => void;
};

const translationFields = [
    { key: 'name', label: generalMessages.name },
    { key: 'address', label: siteMessages.address },
    { key: 'city', label: siteMessages.city },
] as const;

function SiteForm({ onSubmit, form }: SiteFormProps) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const settingsData = React.useContext(SettingsContext);

    const { data: serviceProvidersData } = useListServiceProviders({
        parkingId,
    });

    const { fields } = useFieldArray({
        control: form.control,
        name: 'translations',
    });
    const { translations } = form.getValues();

    React.useEffect(() => {
        if (settingsData?.languages) {
            form.setValue(
                'translations',
                settingsData.languages.map((lang, index) => ({
                    language: lang,
                    name: translations[index]?.name || '',
                    address: translations[index]?.address || '',
                    city: translations[index]?.city || '',
                })),
            );
        }
    }, [form, settingsData]);

    const options = React.useMemo(() => {
        if (!serviceProvidersData) return [];
        return serviceProvidersData.results.map(({ name, id }) => ({
            label: name,
            value: id,
        }));
    }, [serviceProvidersData]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormFieldBlock>
                <FormLabel variant="dialog" required>
                    {formatMessage(siteMessages.coordinates)}
                </FormLabel>
                <FormFieldBlockBody className="flex-1">
                    <div className="flex flex-1 flex-col gap-2 md:mr-4 md:flex-row">
                        <FormField
                            control={form.control}
                            name="coordinates.coordinates.0"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <InputWithIcon
                                            {...field}
                                            icon={faX}
                                            inputMode="decimal"
                                            pattern="^-?\d*(\.?\d{0,16})?$"
                                            placeholder={`${formatMessage(siteMessages.longitude)}...`}
                                            onChange={(e) =>
                                                form.setValue(
                                                    'coordinates.coordinates.0',
                                                    Number(e.target.value) || 0,
                                                    {
                                                        shouldValidate: true,
                                                    },
                                                )
                                            }
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="coordinates.coordinates.1"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <InputWithIcon
                                            {...field}
                                            icon={faY}
                                            inputMode="decimal"
                                            type="text"
                                            pattern="^-?\d*(\.?\d{0,16})?$"
                                            placeholder={`${formatMessage(siteMessages.latitude)}...`}
                                            onChange={(e) =>
                                                form.setValue(
                                                    'coordinates.coordinates.1',
                                                    Number(e.target.value) || 0,
                                                    {
                                                        shouldValidate: true,
                                                    },
                                                )
                                            }
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </FormFieldBlockBody>
            </FormFieldBlock>
            {translationFields.map(({ key, label }) =>
                fields.map((_, index) => (
                    <FormField
                        key={`translations.${index}.${key}`}
                        control={form.control}
                        name={`translations.${index}.${key}`}
                        render={({ field }) => (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog" required>
                                    <FormattedMessage id={label.id} defaultMessage={label.defaultMessage} />
                                </FormLabel>
                                <FormControlStack>
                                    <FormControl>
                                        <InputWithInlineTitle
                                            {...field}
                                            inlineTitle={settingsData?.languages[index]?.toUpperCase() || ''}
                                            placeholder={`${formatMessage(label)}...`}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormControlStack>
                            </FormItem>
                        )}
                    />
                )),
            )}
            <SelectFormField<SiteFormSchemaValues>
                form={form}
                name="type"
                label={formatMessage(siteMessages.type)}
                options={Object.values(SITE_TYPE)}
                optionTranslationMap={SITE_TRANSLATION_MAP}
            />
            <InputFormField<SiteFormSchemaValues>
                form={form}
                name="floors"
                type="number"
                label={formatMessage(siteMessages.floorsCount)}
                min={0}
            />
            <SelectFormField<SiteFormSchemaValues>
                form={form}
                name="country"
                label={formatMessage(siteMessages.country)}
                options={Object.values(COUNTRY_MAP)}
                optionTranslationMap={COUNTRY_TRANSLATION_MAP}
            />
            <SelectFormField<SiteFormSchemaValues>
                form={form}
                name="service_provider"
                label={formatMessage(siteMessages.organisation)}
                options={options}
            />
            <SelectFormField<SiteFormSchemaValues>
                form={form}
                name="is_24_7"
                isBoolean
                required={false}
                label={formatMessage(siteMessages.is247)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
            />
        </Form>
    );
}

export default SiteForm;
